<script>
  import * as yup from 'yup';
  import { createEventDispatcher } from 'svelte';
  import startCase from 'lodash/startCase';
  import isEmpty from 'lodash/isEmpty';

  import { Accordion, AccordionItem, Checkbox, InlineNotification, Modal, Tag, TextArea, TextInput } from 'carbon-components-svelte';
  import WarningFilled from 'carbon-icons-svelte/lib/WarningFilled16';

  import ChangedFeedDifference from './ChangedFeedDifference.svelte';
  import LoadingSpinner from '../../../../components/LoadingSpinner.svelte';

  import { getServerErrorMessage } from '../../../../utils';

  export let open = false,
    feedDifference,
    feedIdsRequiringApprovalSet = new Set(),
    supportsObjectRestore = false,
    selectedRowIds = [],
    onSubmitChanges;

  let approvals = {},
    approvalsValid = true,
    understood = false,
    urlValidationErrors = {},
    loading = false,
    submitError = null;

  const dispatch = createEventDispatcher();
  const urlValidationSchema = yup.object().shape({
    url: yup.string().url('Invalid URL').required('Valid URL is required'),
  });

  const typeToColor = {
    created: 'green',
    updated: 'cyan',
    deleted: 'red',
  };

  function resetValues() {
    approvals = {};
    approvalsValid = true;
    understood = false;
    urlValidationErrors = {};
  }

  function handleClose() {
    open = false;
    resetValues();
  }

  function validateUrl({ id, url }) {
    if (!url) {
      delete urlValidationErrors[id];
      urlValidationErrors = { ...urlValidationErrors };
      return;
    }

    urlValidationSchema
      .validate({ url })
      .then(() => {
        delete urlValidationErrors[id];
        urlValidationErrors = { ...urlValidationErrors };
      })
      .catch((error) => {
        urlValidationErrors = { ...urlValidationErrors, [id]: { error: error?.message ?? 'Invalid URL' } };
      });
  }

  function validApprovals() {
    if (isEmpty(approvals)) {
      return false;
    }

    return Object.keys(approvals).length === requireApproval.length;
  }

  function handleUrlChange({ id, feedId }) {
    return function onCustomChange({ detail: value }) {
      validateUrl({ id, url: value });

      if (!value && !approvals[id]?.note) {
        delete approvals[id];
        approvals = { ...approvals };
        return;
      }

      approvals = { ...approvals, [id]: { ...(approvals[id] ?? {}), feedId, url: value } };
    };
  }

  function handleNoteChange({ id, feedId }) {
    return function onChange({ target: { value } }) {
      if (!value && !approvals[id]?.url) {
        delete approvals[id];
        approvals = { ...approvals };
        return;
      }

      approvals = { ...approvals, [id]: { ...(approvals[id] ?? {}), feedId, note: value } };
    };
  }

  async function handleSubmit(restoreAfterSubmit = false) {
    approvalsValid = true;

    if (requireApproval.length && !validApprovals()) {
      approvalsValid = false;
      return;
    }

    loading = true;
    submitError = null;

    try {
      await onSubmitChanges({ feedDifference, approvals });
      if (restoreAfterSubmit) {
        dispatch('object-restore');
      }

      handleClose();
    } catch (error) {
      console.error('[SaveFeeds] Failed to submit changes!', error);
      submitError = getServerErrorMessage(error) || 'Failed to save group feed permissions.';
    } finally {
      loading = false;
    }
  }

  async function handleSecondaryButtonsClick({ detail: { text } }) {
    switch (text) {
      case 'Cancel':
        handleClose();
        break;
      case `Apply (${totalChanges}) & Restore (${selectedRowIds.length})`:
        await handleSubmit(true);
        break;
    }
  }

  $: requireApproval = Object.values(feedDifference.created).filter(({ feedId }) => feedIdsRequiringApprovalSet.has(feedId)) ?? [];
  $: totalChanges =
    Object.keys(feedDifference.created ?? {}).length +
    Object.keys(feedDifference.updated ?? {}).length +
    Object.keys(feedDifference.deleted ?? {}).length;
  $: primaryButtonDisabled = !totalChanges || !isEmpty(urlValidationErrors) || (requireApproval.length && !understood) || loading;
  $: secondaryButtons =
    supportsObjectRestore && selectedRowIds.length && !primaryButtonDisabled
      ? [
          { text: 'Cancel', disabled: loading },
          {
            text: `Apply (${totalChanges}) & Restore (${selectedRowIds.length})`,
            disabled: primaryButtonDisabled,
          },
        ]
      : [{ text: 'Cancel' }];
</script>

<Modal
  bind:open
  size="lg"
  modalHeading="Confirm changes"
  preventCloseOnClickOutside
  primaryButtonText={`Apply (${totalChanges})`}
  {secondaryButtons}
  {primaryButtonDisabled}
  on:click:button--secondary={handleSecondaryButtonsClick}
  on:close={handleClose}
  on:submit={() => handleSubmit(false)}
>
  <LoadingSpinner small {loading}>
    {#if submitError}
      <InlineNotification kind="error" title="Error:" subtitle={submitError} lowContrast hideCloseButton />
    {/if}
    <div class="tag-align">
      <p>Are you sure you want to apply the following changes?</p>
      {#if requireApproval.length}
        <br />
        <InlineNotification lowContrast hideCloseButton kind="warning">
          <div slot="titleChildren"><strong>Important!</strong></div>
          <div slot="subtitleChildren">
            Feeds marked with this icon <strong>require</strong> exchange approval prior to entitlement. Make sure you have acquired the
            approval <strong>before</strong> you proceed. <br /> <br />
            Example: <Tag type="green"><WarningFilled /> example_feed_name</Tag>
          </div>
        </InlineNotification>
      {/if}
      {#if !approvalsValid}
        <InlineNotification
          kind="error"
          title="Error:"
          subtitle="You need to add a URL or a note for all approvals to proceed"
          lowContrast={true}
          hideCloseButton={true}
        />
      {/if}
    </div>
    {#each ['created', 'updated', 'deleted'] as type}
      {#if Object.keys(feedDifference[type]).length}
        <div class="change-type-wrapper tag-align">
          <h5>{startCase(type)}</h5>
          <Accordion size="sm">
            <ul>
              {#each Object.values(feedDifference[type]) as { id, feedId, name, startDate, endDate, difference }}
                {#if type === 'created' && feedIdsRequiringApprovalSet.has(feedId)}
                  <AccordionItem open>
                    <div slot="title">
                      <Tag type={typeToColor[type]}><WarningFilled /> {name}</Tag>
                      {#if difference}
                        <ChangedFeedDifference {difference} />
                      {:else}
                        <strong>{startDate} &mdash; {endDate}</strong>
                      {/if}
                    </div>
                    <div>
                      <TextInput
                        value={approvals[id]?.url}
                        invalid={urlValidationErrors[id]}
                        invalidText={urlValidationErrors[id]?.error}
                        on:change={handleUrlChange({ id, feedId })}
                        hideLabel
                        size="sm"
                        labelText="URL to approval"
                        placeholder="Link to approval"
                      />
                      <TextArea
                        value={approvals[id]?.note}
                        on:change={handleNoteChange({ id, feedId })}
                        hideLabel
                        rows={3}
                        labelText="Notes"
                        placeholder="Notes"
                      />
                    </div>
                  </AccordionItem>
                {:else}
                  <li class="li">
                    <div class="li-content">
                      <Tag type={typeToColor[type]}>{name}</Tag>
                      {#if difference}
                        <ChangedFeedDifference {difference} />
                      {:else}
                        <strong>{startDate} &mdash; {endDate}</strong>
                      {/if}
                    </div>
                  </li>
                {/if}
              {/each}
            </ul>
          </Accordion>
        </div>
      {/if}
    {/each}
    {#if requireApproval.length}
      <div class="required">
        <Checkbox labelText="I confirm that I have the approvals required for this entitlement to proceed" bind:checked={understood} />
      </div>
    {/if}
  </LoadingSpinner>
</Modal>

<style>
  .change-type-wrapper:not(:first-child) {
    margin-top: 2rem;
  }

  .change-type-wrapper .li {
    margin-left: 1rem;
    min-height: 2rem;
    padding: 0.3125rem 0;
  }

  .change-type-wrapper :global(li li:last-child) {
    margin-bottom: 1rem;
  }

  .change-type-wrapper :global(.bx--accordion__item) {
    border-top: unset !important;
    border-bottom: unset !important;
  }

  .change-type-wrapper :global(.bx--accordion__content) {
    margin-left: 1.5rem;
  }

  .change-type-wrapper :global(textarea) {
    margin-top: 0.25rem;
  }

  .tag-align :global(.bx--tag span) {
    display: flex;
  }

  .tag-align :global(.bx--tag span svg) {
    margin-right: 0.15rem;
    width: 12px;
  }

  .required {
    position: fixed;
    bottom: 75px;
    background-color: var(--cds-ui-01);
  }
</style>
